import React from 'react';
import { Modal, Button, Card } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';

/**
 * CustomerReviewModal - Shows detailed customer reviews
 * 
 * Props:
 * - show: boolean - whether modal is visible
 * - onHide: function - called when modal is closed
 * - reviewData: object - contains reviews, averageRating, totalReviews
 * - customerInfo: object - customer name, phone, email
 */
export default function CustomerReviewModal({ show, onHide, reviewData, customerInfo }) {
  if (!reviewData || !reviewData.hasReviews) {
    return null;
  }

  const renderStars = (rating) => {
    return (
      <div className="d-inline-flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            size={16}
            color={star <= rating ? '#ffc107' : '#e4e5e9'}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCompanyName = (driver) => {
    if (!driver) return 'Unknown Company';
    return driver.companyName || 'Unknown Company';
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: '#ffc107', color: '#000' }}>
        <Modal.Title>
          <FaStar className="me-2" />
          Customer Reviews
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {customerInfo && (
          <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h6 className="mb-1">
              <strong>{customerInfo.name}</strong>
            </h6>
            <div className="text-muted small">
              {customerInfo.phone && <div>📞 {customerInfo.phone}</div>}
              {customerInfo.email && <div>📧 {customerInfo.email}</div>}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="mb-4 text-center">
          <div className="d-inline-flex align-items-center">
            <FaStar size={32} color="#ffc107" />
            <span className="ms-2 fs-4 fw-bold">{reviewData.averageRating}</span>
            <span className="ms-1 text-muted">/ 5</span>
          </div>
          <div className="text-muted">
            Based on {reviewData.totalReviews} review{reviewData.totalReviews !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Reviews List */}
        <div className="mb-3">
          <h6>Recent Reviews</h6>
          {reviewData.reviews.length === 0 ? (
            <div className="text-muted text-center py-3">
              No reviews available
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {reviewData.reviews.map((review, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body className="py-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div className="d-flex align-items-center mb-1">
                          {renderStars(review.starRating)}
                          <span className="ms-2 fw-bold">{review.starRating}/5</span>
                        </div>
                        <div className="text-muted small">
                          by {getCompanyName(review.driverId)} - {formatDate(review.createdAt)}
                        </div>
                      </div>
                    </div>
                    
                    {review.reviewText && (
                      <div className="mt-2">
                        <p className="mb-0 small fst-italic">{review.reviewText}</p>
                      </div>
                    )}
                    
                    {review.completionReason && (
                      <div className="mt-2">
                        <p className="mb-0 small text-warning">
                          <strong>⚠️ Low Rating Reason:</strong> {review.completionReason}
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button variant="secondary" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
